"use client";
import { useApp, useAppStore } from "@/store/app";
import {
  useReplicacheFriendIdMap,
  useReplicacheStore,
} from "@/store/replicache";
import type { User, UserId } from "@/utils/types";
import Image from "next/image";
import { useEffect, useState } from "react";
import shallow from "zustand/shallow";

interface FriendProps {
  friendId: UserId;
  friend?: User;
}

// self/friend
const LOG_TAG = "👫 Friend";

export default function Friend({ friendId, friend }: FriendProps) {
  const { userProfileGet, signIn } = useApp();
  const { user } = useAppStore(({ user }) => ({ user }), shallow);
  const { friendAdd, friendRemove } = useReplicacheStore();
  const { friendIdMap } = useReplicacheFriendIdMap();
  const friendIdMapRecord = friendIdMap[friendId];
  const [profile, setProfile] = useState(friend);

  useEffect(() => {
    if (friend) return;
    const fn = async () => {
      const userProfile = await userProfileGet(friendId);
      setProfile(userProfile);
    };
    fn();
  }, [friend, friendId, userProfileGet]);

  const handleFriendAdd = async (friend: User) => {
    if (!friend) {
      signIn();
      return;
    }
    await friendAdd({ friend, ts: Date.now() });
    console.debug(LOG_TAG, "friendAdd DONE", { friend });
  };

  const handleFriendRemove = async (friend: User) => {
    if (!friend) {
      signIn();
      return;
    }
    await friendRemove({ friendId: friend.id, ts: Date.now() });
    console.debug(LOG_TAG, "friendRemove DONE", { friend });
  };

  if (!profile) return null;

  const isCurrentFriend = friendIdMapRecord?.deleted_timestamp === 0;
  const isFriendRequest = friendIdMapRecord?.deleted_timestamp === -1;
  const isDeletedFriend = friendIdMapRecord?.deleted_timestamp > 0;
  const isNotFriendYet =
    !friendIdMapRecord || isFriendRequest || isDeletedFriend;

  // console.debug(LOG_TAG, {
  //   email: profile.email,
  //   friendIdMapRecord,
  //   isFriendRequest,
  //   isNotFriendYet,
  // });

  return (
    <div
      className="p-8 max-w-[400px] mb-4 bg-base-300 rounded-lg"
      title={profile.displayName}
    >
      <div className="mb-4">
        <Image
          unoptimized
          className="rounded-lg m-auto"
          height={300}
          width={300}
          referrerPolicy="no-referrer"
          src={`${profile.photoUrl || "/logo-256.png"}`}
          alt=""
        />
      </div>
      <div>
        <h3 className="text-xl">{profile.displayName || "Anonymous"}</h3>
        <a href="mailto:${profile.email}">{profile.email}</a>
        {isFriendRequest && (
          <div className="mt-2 text-sm text-warning">Confirmation pending</div>
        )}
      </div>
      {user && (
        <div className="flex justify-start mt-4">
          {isNotFriendYet && (
            <div className="mr-4">
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleFriendAdd(profile)}
                type="button"
              >
                {isFriendRequest ? "Accept" : "Add friend"}
              </button>
            </div>
          )}
          {(isCurrentFriend || isFriendRequest) && (
            <div>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => handleFriendRemove(profile)}
                type="button"
              >
                {isFriendRequest ? "Decline" : "Remove friend"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
